// PACKAGES
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BackupSessionInfo, ContentsSort, FileInfo, InvData, MediaInfo, PathInfo } from '../dashboard/file-explorer-page/file-explorer-page.types';
// APP
import { API_ENDPOINT } from '../_config/config';

const PAGE_SIZE = 200;

/** a page of a folder's contents, folders first */
export interface ContentsPage {
  contents: (FileInfo | PathInfo)[];
  /** folders plus files; set on the first page only */
  count: number | null;
  /** set on the first page only */
  folderCount: number | null;
  /** null on the last page */
  nextCursor: string | null;
}

export type PageQuery = ContentsSort & { cursor: string | null };

/** query params for one page; the first page has no cursor */
function pageParams({ sortBy, sortOrder, cursor }: PageQuery) {
  const params = new HttpParams().set('limit', PAGE_SIZE).set('sortBy', sortBy).set('sortOrder', sortOrder);
  return cursor ? params.set('cursor', cursor) : params;
}

interface FileExplorerSubjectState {
  activeDirectory: InvData;
  selectedContent: InvData[];
}

@Injectable({
  providedIn: 'root'
})
export class FileExplorerService {
  state = new BehaviorSubject<FileExplorerSubjectState>({ activeDirectory: null, selectedContent: [] });
  
  constructor(private http: HttpClient) { }

  /** Get Project Media and Backup Sessions */
  getProjectMedia(projectId: string) {
    return this.http.get<{
      count: { media: number, backupSessions: number }, 
      media: MediaInfo[],
      backupSessions: BackupSessionInfo[] 
    }>(`${API_ENDPOINT}/projects/${projectId}/media`);
  }

  /** Get a page of a backup session's root folders, then the files at its root */
  getRootFolders(projectId: string, backupSessionId: string, query: PageQuery): Observable<ContentsPage> {
    const params = pageParams(query).append('backupSessionId', encodeURIComponent(backupSessionId));

    return this.http.get<Omit<ContentsPage, 'contents'> & { directories: PathInfo[], files?: FileInfo[] }>(`${API_ENDPOINT}/projects/${projectId}/directories/roots`, { params })
      .pipe(map(({ directories, files, ...page }) => ({ ...page, contents: [...directories, ...(files ?? [])] })));
  };

  /** Get a page of a folder's contents */
  getFolderContents(projectId: string, directoryId: string, query: PageQuery): Observable<ContentsPage> {
    return this.http.get<ContentsPage & { parentDirectoryId: string }>
    (`${API_ENDPOINT}/projects/${projectId}/directories/${encodeURIComponent(directoryId)}/contents`, { params: pageParams(query) });
  }
  
  setActiveDirectory(directory: InvData) {
    this.state.next({
      activeDirectory: directory,
      selectedContent: [],
    });
  }

  get explorerState() {
    return this.state.asObservable();
  }

}
